import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Shield, Eye, Ruler, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang, route } from "../i18n";
import { useSiteServices, useSitePosts, useCompany } from "../components/SiteContext";
import SiteImage from "../components/SiteImage";
import { CREDENTIALS, CREDENTIALS_NOTE } from "../data/config";
import { MaskedLines, Reveal, DrawnLine } from "../components/Reveal";
import { MaskedLine } from "../components/MaskedLine";
import { ButtonPrimary, ButtonSecondary } from "../components/Buttons";
import { Marquee } from "../components/Marquee";
import { SectionHead } from "../components/SectionHead";
import { AviahWatermark } from "../components/AviahMark";
import StrategyDiagram from "../components/StrategyDiagram";
import FounderTimeline from "../components/FounderTimeline";
import ProcessSteps from "../components/ProcessSteps";
import ServiceCard from "../components/ServiceCard";
import BlogCard from "../components/BlogCard";
import FinalCta from "../components/FinalCta";
import { HERO_DELAY } from "../lib/intro";
import { track } from "../lib/track";

const Hero = () => {
  const { lang, t } = useLang();
  const h = t.home.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#F4F0E8]" data-testid="hero">
      <AviahWatermark className="absolute -right-24 top-24 h-96 w-96 text-[#171717] opacity-[0.04]" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-28 sm:px-8 sm:pt-36 lg:pb-24">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: HERO_DELAY + 0.1 }}
              className="eyebrow text-[#8a6f3e]"
              data-testid="hero-eyebrow"
            >
              {h.eyebrow}
            </motion.p>
            <MaskedLines
              lines={h.lines}
              delay={HERO_DELAY + 0.25}
              className="mt-8 max-w-4xl text-4xl font-light leading-[1.06] tracking-tight text-[#171717] sm:text-5xl lg:text-6xl"
            />
            <Reveal delay={HERO_DELAY + 0.75} y={20}>
              <p className="mt-8 max-w-xl text-[0.95rem] leading-relaxed text-[#171717]/75 sm:text-base">{h.body}</p>
            </Reveal>
            <Reveal delay={HERO_DELAY + 0.9} y={18}>
              <div className="mt-10 flex flex-wrap gap-4">
                <ButtonPrimary to={route(lang, "contact")} testid="hero-cta-primary" event="book_consultation_click">
                  {h.primary}
                </ButtonPrimary>
                <ButtonSecondary to={route(lang, "services")} tone="light" testid="hero-cta-secondary">
                  {h.secondary}
                </ButtonSecondary>
              </div>
            </Reveal>
            <Reveal delay={HERO_DELAY + 1.05} y={12}>
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-[0.65rem] uppercase tracking-[0.18em] text-[#171717]/50">
                {h.micro.map((m, i) => (
                  <span key={i} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#C6A46A]" />
                    {m}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <motion.div style={{ y: bgY }} className="relative hidden lg:block" aria-hidden="true">
            <Reveal delay={HERO_DELAY + 0.6} y={24}>
              <div className="relative">
                <div className="absolute -left-3 -top-3 h-full w-full border border-[#C6A46A]" />
                <SiteImage
                  slot="hero"
                  alt="Modern architectural interior in warm light"
                  className="relative overflow-hidden"
                  imgClassName="aspect-[4/5] w-full object-cover"
                  eager
                  badge={false}
                />
              </div>
            </Reveal>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: HERO_DELAY + 1.4, duration: 0.8 }}
        className="relative mt-4 flex flex-col items-center gap-3 pb-8"
        aria-hidden="true"
      >
        <span className="text-[0.55rem] uppercase tracking-[0.3em] text-[#171717]/40">{h.scroll}</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-[#C6A46A]" style={{ animationDuration: "2.4s" }} />
      </motion.div>
    </section>
  );
};

const TrustStrip = () => {
  const { lang, t } = useLang();
  const items = CREDENTIALS[lang];
  return (
    <section className="border-y border-[#F4F0E8]/10 bg-[#171717]" data-testid="trust-strip">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <Reveal>
          <p className="eyebrow text-center text-[#C6A46A]">{t.home.trust.eyebrow}</p>
          <h2 className="mt-4 text-center text-xl font-light tracking-tight text-[#F4F0E8] sm:text-2xl">
            {t.home.trust.title}
          </h2>
        </Reveal>
        <div className="mt-10 flex flex-wrap items-stretch justify-center gap-px overflow-hidden border border-[#F4F0E8]/10 bg-[#F4F0E8]/10">
          {items.map((c, i) => (
            <div key={i} className="flex-1 basis-48 bg-[#171717] px-5 py-5 text-center" data-testid={`credential-${i}`}>
              <span className="text-[0.68rem] uppercase leading-relaxed tracking-[0.14em] text-[#F4F0E8]/75">{c}</span>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-[0.65rem] leading-relaxed text-[#F4F0E8]/35">
          {CREDENTIALS_NOTE[lang]}
        </p>
      </div>
    </section>
  );
};

const Numbers = () => {
  const { t } = useLang();
  const n = t.home.numbers;
  return (
    <section className="bg-[#F4F0E8]" data-testid="more-than-numbers">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="eyebrow text-[#8a6f3e]">{n.chapter}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="sr-only">{n.title}</h2>
              <p aria-hidden="true" className="mt-6 max-w-xl text-2xl font-light leading-snug tracking-tight text-[#171717] sm:text-3xl">
                {n.title}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{n.p1}</p>
              <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{n.p2}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:pt-16">
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full border border-[#C6A46A]" aria-hidden="true" />
              <SiteImage
                slot="staircase"
                className="relative overflow-hidden"
                imgClassName="aspect-[4/3] w-full object-cover grayscale-[30%]"
              />
            </div>
            <blockquote className="mt-10 border-l-2 border-[#C6A46A] pl-6">
              <p className="text-xl font-light italic leading-snug tracking-tight text-[#171717] sm:text-2xl">
                “{n.quote}”
              </p>
            </blockquote>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Strategy = () => {
  const { t } = useLang();
  const s = t.home.strategy;
  return (
    <section className="relative overflow-hidden bg-[#171717]" data-testid="integrated-strategy">
      <AviahWatermark className="absolute -left-28 top-10 h-80 w-80 opacity-[0.05]" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHead chapter={2} eyebrow={s.chapter} title={s.title} copy={s.copy} />
        <div className="mt-16">
          <StrategyDiagram />
        </div>
      </div>
    </section>
  );
};

const ServicesSec = () => {
  const { t } = useLang();
  const services = useSiteServices();
  const s = t.home.services;
  return (
    <section className="bg-[#F4F0E8]" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHead chapter={3} eyebrow={s.chapter} title={s.title} copy={s.copy} tone="light" />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((svc, i) => (
            <ServiceCard key={svc.slug} service={svc} delay={i * 0.08} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Founder = () => {
  const { lang, t } = useLang();
  const f = t.home.founder;
  return (
    <section className="relative overflow-hidden bg-[#EEE9DF]" data-testid="founder-section">
      <AviahWatermark className="absolute -right-24 bottom-0 h-96 w-96 text-[#171717] opacity-[0.04]" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <SectionHead chapter={4} eyebrow={f.chapter} tone="light" />
          <h2 className="mt-8 text-3xl font-light leading-[1.1] tracking-tight text-[#171717] sm:text-4xl lg:text-5xl">
            {f.titleLines.map((line, i) => (
              <MaskedLine key={i} delay={i * 0.12}>
                {line}
              </MaskedLine>
            ))}
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{f.p1}</p>
            <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{f.p2}</p>
            <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{f.p3}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10">
              <ButtonSecondary to={route(lang, "author")} tone="light" testid="founder-cta">
                {f.cta}
              </ButtonSecondary>
            </div>
          </Reveal>
        </div>
        <div className="mt-24 sm:mt-32">
          <FounderTimeline stages={f.stages} finale={f.finale} />
        </div>
      </div>
    </section>
  );
};

const Mission = () => {
  const { t } = useLang();
  const m = t.home.mission;
  return (
    <section className="bg-[#F4F0E8]" data-testid="mission-vision">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid gap-16 lg:grid-cols-2">
          <div className="border-t border-[#171717]/15 pt-10">
            <Reveal>
              <p className="eyebrow text-[#8a6f3e]">{m.missionLabel}</p>
              <p className="mt-6 text-3xl font-light leading-tight tracking-tight text-[#171717] sm:text-4xl">
                {m.missionWords}
              </p>
              <p className="mt-6 max-w-md text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{m.missionCopy}</p>
            </Reveal>
          </div>
          <div className="border-t border-[#171717]/15 pt-10">
            <Reveal delay={0.15}>
              <p className="eyebrow text-[#8a6f3e]">{m.visionLabel}</p>
              <p className="mt-6 max-w-md text-xl font-light leading-relaxed tracking-tight text-[#171717] sm:text-2xl">
                {m.visionCopy}
              </p>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.2}>
          <p className="mt-20 text-center text-2xl font-light uppercase leading-snug tracking-[0.08em] text-[#171717] sm:text-3xl lg:text-4xl">
            {m.line}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Serve = () => {
  const { t } = useLang();
  const s = t.home.serve;
  return (
    <section className="bg-[#F4F0E8]" data-testid="who-we-serve">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHead chapter={5} eyebrow={s.chapter} title={s.title} tone="light" />
        <div className="mt-16 grid gap-px border border-[#171717]/10 bg-[#171717]/10 sm:grid-cols-2 lg:grid-cols-3">
          {s.items.map((item, i) => (
            <div key={i} className="group bg-[#F4F0E8] p-8 transition-colors duration-300 hover:bg-[#EEE9DF] sm:p-10" data-testid={`serve-${i}`}>
              <span className="text-[0.6rem] font-medium tracking-[0.2em] text-[#C6A46A]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 text-lg font-light uppercase tracking-[0.12em] text-[#171717]">{item.t}</h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-[#171717]/60">{item.d}</p>
              <span className="mt-6 block h-px w-8 bg-[#C6A46A]/60 transition-all duration-500 group-hover:w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Method = () => {
  const { t } = useLang();
  const m = t.home.method;
  return (
    <section className="bg-[#F4F0E8]" data-testid="aviah-method">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHead chapter={6} eyebrow={m.chapter} title={m.title} tone="light" />
        <div className="mt-16">
          <ProcessSteps steps={m.steps} tone="light" />
        </div>
      </div>
    </section>
  );
};

const Privacy = () => {
  const { t } = useLang();
  const p = t.home.privacy;
  const icons = [Shield, Eye, Ruler];
  return (
    <section className="grain relative overflow-hidden bg-[#1E1E1E]" data-testid="privacy-section">
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <DrawnLine className="mx-auto mb-10 max-w-16" />
            <h2 className="text-3xl font-light leading-[1.15] tracking-tight text-[#F4F0E8] sm:text-4xl lg:text-5xl">
              {p.title}
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-[0.95rem] leading-relaxed text-[#F4F0E8]/60 sm:text-base">{p.copy}</p>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-px border border-[#F4F0E8]/10 bg-[#F4F0E8]/10 sm:grid-cols-3">
          {p.pillars.map((pillar, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={i} delay={i * 0.12}>
                <div className="h-full bg-[#1E1E1E] p-10 text-center" data-testid={`pillar-${i}`}>
                  <Icon className="mx-auto h-5 w-5 text-[#C6A46A]" strokeWidth={1.5} />
                  <h3 className="mt-5 text-sm font-medium uppercase tracking-[0.2em] text-[#F4F0E8]">{pillar.t}</h3>
                  <p className="mt-3 text-[0.95rem] leading-relaxed text-[#F4F0E8]/55">{pillar.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Why = () => {
  const { t } = useLang();
  const w = t.home.why;
  return (
    <section className="bg-[#F4F0E8]" data-testid="why-aviah">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHead chapter={7} eyebrow={w.chapter} title={w.title} tone="light" />
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {w.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="border-t border-[#171717]/15 pt-6" data-testid={`why-${i}`}>
                <span className="text-2xl font-light text-[#C6A46A]">{item.n}</span>
                <h3 className="mt-3 text-base font-normal uppercase tracking-[0.12em] text-[#171717]">{item.t}</h3>
                <p className="mt-3 text-[0.95rem] leading-relaxed text-[#171717]/65">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Location = () => {
  const { lang, t } = useLang();
  const company = useCompany();
  const l = t.home.location;
  const a = company.address;
  return (
    <section className="relative overflow-hidden bg-[#EEE9DF]" data-testid="location-section">
      <AviahWatermark className="absolute -left-20 bottom-0 h-80 w-80 text-[#171717] opacity-[0.04]" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <div>
            <SectionHead chapter={8} eyebrow={l.chapter} title={l.title} tone="light" />
            <Reveal delay={0.2}>
              <address className="mt-8 flex items-start gap-3 text-sm not-italic leading-relaxed text-[#171717]/75">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A46A]" strokeWidth={1.5} />
                <span>
                  {a.street}, {a.suite}
                  <br />
                  {a.city}, {a.state} {a.zip}
                </span>
              </address>
              <p className="mt-4 text-sm text-[#171717]/60">
                {l.hoursLabel}: {company.officeHours.days[lang]} · {company.officeHours.open}–{company.officeHours.close}
              </p>
              <ul className="mt-8 space-y-3">
                {l.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#171717]/70">
                    <span className="h-px w-5 bg-[#C6A46A]" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-4">
                <ButtonPrimary to={route(lang, "contact")} testid="location-book" event="book_consultation_click">
                  {l.book}
                </ButtonPrimary>
                <ButtonSecondary href={a.mapsQuery} tone="light" testid="location-directions" event="directions_click">
                  {l.directions}
                </ButtonSecondary>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="border border-[#171717]/10">
              <iframe
                title="Aviah Financial — Lake Nona office map"
                src={a.mapsEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="aspect-[4/3] w-full grayscale-[60%] contrast-[1.05]"
                data-testid="location-map"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Insights = () => {
  const { lang, t } = useLang();
  const allPosts = useSitePosts();
  const s = t.home.insights;
  const posts = allPosts.filter((p) => p.status === "published").slice(0, 3);
  return (
    <section className="bg-[#F4F0E8]" data-testid="insights-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead chapter={9} eyebrow={s.chapter} title={s.title} copy={s.copy} tone="light" />
          <Reveal delay={0.2}>
            <ButtonSecondary to={route(lang, "blog")} tone="light" testid="insights-cta">
              {s.cta}
            </ButtonSecondary>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {posts.map((p) => (
            <BlogCard key={p.slug} post={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { t } = useLang();
  const company = useCompany();
  const seo = t.home.seo;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "ProfessionalService"],
        name: company.name,
        url: typeof window !== "undefined" ? window.location.origin : undefined,
        telephone: company.phone,
        address: {
          "@type": "PostalAddress",
          streetAddress: `${company.address.street}, ${company.address.suite}`,
          addressLocality: company.address.city,
          addressRegion: company.address.state,
          postalCode: company.address.zip,
          addressCountry: "US",
        },
        founder: { "@type": "Person", name: company.founder },
        openingHours: "Mo-Fr 09:00-17:00",
      },
      { "@type": "WebSite", name: company.name, inLanguage: ["en", "es"] },
    ],
  };

  return (
    <>
      <Seo title={seo.title} siteName={company.name} description={seo.description} jsonLd={jsonLd} />
      <Hero />
      <Marquee items={t.home.marquee} tone="light" />
      <TrustStrip />
      <Numbers />
      <Strategy />
      <ServicesSec />
      <Founder />
      <Mission />
      <Serve />
      <Method />
      <Privacy />
      <Why />
      <Location />
      <Insights />
      <FinalCta />
    </>
  );
};

export default Home;
